/**
 * **Reprint a transfer** — the cutting bench's door to the shared reprint queue, per TRANSFER, by sheet.
 * Moved out of the retired run-sheet overlay verbatim (`docs/pull-list-plan-v1.md`); the selection
 * model, the one-POST-per-garment add and its partial-failure accounting are unchanged.
 *
 * It is the door for a film torn BEFORE pressing, which is why "keep the blank" is defaulted ON here
 * and OFF at the scan station's defect flow (a scorch there really does ruin the garment). Screen-only:
 * never printed, so its portal id is deliberately absent from the print-isolation chain in globals.css.
 */
import { reprintPlacementsFor } from '@fsp/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Loader2, Repeat2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import {
  applyQueueAddOutcomes,
  type BatchManifest,
  formatInches,
  garmentKey,
  groupTransfersBySheet,
  queueAddsForSelection,
  type ReprintSelection,
  toggleTransferSelection,
} from './batch-lib';
import { orderCardSearch } from './order-detail-lib';

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn('px-3 py-2 text-left text-xs font-medium text-muted-foreground', className)}>
      {children}
    </th>
  );
}

export function ReprintPicker({
  batchId,
  label,
  blocked,
  onClose,
}: {
  batchId: string;
  label: string;
  /**
   * Why the per-transfer picker is unavailable, or null when it is offered. TWO shapes reach it, which
   * is why this carries COPY rather than a boolean: a sheet that is itself a reprint (its copy numbers
   * do not name the original order's garments — `reissueRanks` is not on the manifest), and a FILMLESS
   * screen-print run, which has no transfers to pick at all.
   */
  blocked: { title: string; detail: string } | null;
  onClose: () => void;
}) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  // Per-garment reprint selection, keyed by `garmentKey` (store#order#line#copyIndex) so one copy of
  // a qty>1 line can be picked without the others. Only the "by sheet · cutting" view exposes it (the
  // only per-transfer rendering).
  const [selected, setSelected] = useState<Map<string, ReprintSelection>>(new Map());
  /**
   * "Keep the blank" (transfer-only): the shirt is fine and only its film was ruined, so the reprint
   * presses onto the EXISTING garment and no replacement blank is drawn for it.
   *
   * ⚠️ Keyed by `garmentKey`, the SAME key as the selection — per garment, not per line. It used to be
   * per line, which the reissue body forced (its transferOnly is per line entry). The queue stores it
   * per row, and `true` is a one-way downgrade at the storage layer: a qty-5 line with one torn
   * transfer and one scorched blank, ticked through a single per-line checkbox, would send true for
   * both, the flush would AND to true, and mark-printed would draw ZERO replacement blanks — leaving
   * the presser a fresh transfer and no shirt.
   *
   * ⚠️ DEFAULTED ON at this station, which INVERTS the safe default everywhere else. The justification
   * is physical: cutting happens before pressing, so the shirt is not in the cutter's hands at all and
   * a torn film cannot have damaged it. Leaving it off meant the feature only worked when someone
   * remembered to tick it, and forgetting is the common case on a busy day — so the default that
   * matches reality is the one that gets it right more often.
   *
   * The mis-set direction is also the cheap one, and self-correcting: wrongly keeping a blank means the
   * presser reaches a garment with no shirt to press it onto and pulls one, whereas wrongly drawing a
   * blank quietly spends a shirt nobody notices. The press kiosk keeps the OFF default — a scorch there
   * really does ruin the garment.
   */
  const [keepBlank, setKeepBlank] = useState<Set<string>>(new Set());
  const toggleKeepBlank = (key: string) =>
    setKeepBlank((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  /**
   * Tick or untick ONE transfer of one garment. `ref` carries exactly the location that was clicked.
   *
   * Per PLACEMENT, not per garment: the nester puts a front and a back far apart or on different
   * sheets, so tearing one film at the cutting bench leaves the other perfectly good. The garment is
   * still the queue's identity — one row, one shirt — but the row now records WHICH films were ruined.
   *
   * Unticking the last film deselects the garment entirely and drops its keep-the-blank answer with
   * it; leaving a stale answer behind would silently apply it to a later re-tick.
   */
  const toggleTransfer = (key: string, ref: ReprintSelection) => {
    // Both pieces of state come from ONE pure call against ONE snapshot, so they cannot end up
    // disagreeing about a garment. `keepBlankByDefault` is this station's policy — see the state
    // declaration above for why the cutting bench inverts the app-wide default.
    const next = toggleTransferSelection({ selected, keepBlank }, key, ref, {
      keepBlankByDefault: true,
    });
    setSelected(next.selected);
    setKeepBlank(next.keepBlank);
  };
  // Escape closes the overlay (matches the app's dialog behaviour).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const manifestQuery = useQuery({
    queryKey: ['batch-manifest', batchId],
    queryFn: async () => {
      const { data, error } = await client.GET('/batches/{batchId}/manifest', {
        params: { path: { batchId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the reprint picker.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data as BatchManifest;
    },
  });

  /** Per-garment result of the last "Add to reprint queue", so a partial failure is legible. Carries
   *  its own label because a successful garment is dropped from the selection the label came from. */
  const [addOutcomes, setAddOutcomes] = useState<
    Array<{
      key: string;
      label: string;
      ok: boolean;
      merged?: boolean;
      /** Garments of this line already queued off another run — an amber note, never a refusal. */
      otherRuns?: number;
      detail?: string;
    }>
  >([]);

  /**
   * Add every selected garment to the SHARED reprint queue (P6) — the cutting bench's entry point.
   *
   * ⚠️ One POST per garment, because the queue's identity is the physical shirt and there is no bulk
   * route. That makes a PARTIAL failure the normal failure, and the accounting below is the whole
   * safety property: a garment with no queue row has nothing blocking its order from the ship lanes,
   * so `Promise.all` + one catch + the old clear-on-success would send that box out a shirt short
   * with nothing anywhere saying so. Successes clear; failures stay ticked and stay named.
   *
   * Sequential rather than concurrent: N is a handful, the server writes one production event per
   * add, and a failure part-way through leaves nothing else in flight to reason about.
   */
  const queueAddMut = useMutation({
    mutationFn: async () => {
      const adds = queueAddsForSelection([...selected.values()], {
        sourceBatchId: batchId,
        keepBlank,
      });
      const labelByKey = new Map(selectedGarments.map((g) => [g.key, g.label]));
      const outcomes: Array<{
        key: string;
        label: string;
        ok: boolean;
        merged?: boolean;
        otherRuns?: number;
        detail?: string;
      }> = [];
      for (const { key, body } of adds) {
        const label = labelByKey.get(key) ?? key;
        try {
          const { data, error } = await client.POST('/batches/reprint-cart/entries', { body });
          // The server's own sentence, verbatim — the D7 screen-print refusal is the only copy that
          // tells the operator what to do instead.
          if (error) {
            outcomes.push({
              key,
              label,
              ok: false,
              detail: error?.error?.detail ?? 'Could not queue it.',
            });
            continue;
          }
          if (!data?.ok) {
            outcomes.push({ key, label, ok: false, detail: 'Empty response from the server.' });
            continue;
          }
          outcomes.push({
            key,
            label,
            ok: true,
            merged: data.data.merged,
            // How many garments of the SAME LINE are already queued off a DIFFERENT production run. A count
            // only, deliberately: ranks restart when a line is re-batched, so nothing here identifies a
            // specific shirt and copy that named one would be asserting an identity the data cannot support.
            otherRuns: data.data.alreadyOutstanding?.length,
          });
        } catch (e) {
          outcomes.push({
            key,
            label,
            ok: false,
            detail: e instanceof Error ? e.message : 'Network error.',
          });
        }
      }
      // The attempted set travels with the result so `onSuccess` never has to re-derive it from a
      // state value the closure captured before the writes.
      return { outcomes, attempted: adds.map((a) => a.garment) };
    },
    onSuccess: ({ outcomes, attempted }) => {
      setAddOutcomes(outcomes);
      // Drop ONLY what actually landed. Anything that failed stays ticked, with its own reason, so
      // the operator can retry it rather than discover the gap on the queue page — or not at all.
      // Computed once from the values the request was built from, which cannot have moved: every
      // control that edits them (the tick boxes, Clear, the keep-blank boxes) is disabled while the
      // adds are in flight.
      const next = applyQueueAddOutcomes(selected, keepBlank, outcomes);
      setSelected(next.selected);
      setKeepBlank(next.keepBlank);
      // ⚠️ NOT ['batches'] — a queue add writes no batch row, so that is the one key that did not
      // move. What moved is the queue, the order's outstanding-reprint lookup (the ship gate's
      // second leg) and the board.
      void queryClient.invalidateQueries({ queryKey: ['reprint-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['production-board'] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      for (const g of attempted) {
        void queryClient.invalidateQueries({ queryKey: ['order-reprints', g.storeId, g.orderId] });
        void queryClient.invalidateQueries({ queryKey: ['order', g.storeId, g.orderId] });
      }
    },
  });

  const m = manifestQuery.data;
  const sheets = m ? groupTransfersBySheet(m) : [];

  /**
   * Every print each garment carries ON THIS SHEET, by `garmentKey`. Feeds `allLocations` on a ticked
   * selection, which is what lets the panel say "reprints ALL 2 prints" without a round trip.
   *
   * ⚠️ `fromStock` transfers are EXCLUDED, matching the server exactly: a placement pulled off the
   * printed-transfer shelf has no film on this sheet to reprint, so the queue-add route leaves it out
   * of the frozen `placements` it stores. Counting it here would make the panel promise a print the
   * sheet will not carry — the confirm dialog and the film disagreeing is precisely the failure the
   * shared scope function exists to prevent.
   */
  const locationsByGarment = new Map<string, string[]>();
  for (const t of m?.transfers ?? []) {
    if (t.fromStock) continue;
    const k = garmentKey(t);
    const arr = locationsByGarment.get(k);
    if (!arr) locationsByGarment.set(k, [t.location]);
    else if (!arr.includes(t.location)) arr.push(t.location);
  }

  // The garments currently ticked, each with its own keep-blank key — one row per PHYSICAL SHIRT, so
  // "garment 2 of 5 keeps its blank, garment 4 doesn't" is expressible. The rank is in the label
  // because that is the only thing distinguishing two rows of the same line.
  const selectedGarments = [...selected.values()]
    .map((ref) => {
      const t = m?.transfers.find(
        (x) => x.storeId === ref.storeId && x.orderId === ref.orderId && x.lineId === ref.lineId,
      );
      const key = garmentKey(ref);
      const name = t ? `${t.orderName} · ${t.title}` : `${ref.orderId} · ${ref.lineId}`;
      return {
        key,
        lineKey: `${ref.storeId}#${ref.orderId}#${ref.lineId}`,
        label: `${name} · garment ${ref.copyIndex + 1}`,
        // What the server's scope function will be handed for THIS garment. Grouped into a line-level
        // answer below — never used on its own.
        row: {
          placements: ref.allLocations.map((location) => ({ location })),
          transferOnly: keepBlank.has(key) ? true : undefined,
          ruinedLocations: ref.locations,
        },
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  /**
   * The reprint scope PER ORDER LINE, from the SAME function the server's cost quote and sheet build
   * use — so the sentence beside each keep-the-blank checkbox states the real consequence BEFORE the
   * operator submits, and unticking flips it on the same tap.
   *
   * ⚠️ PER LINE, not per garment. A batch item row is keyed per (line, location) with the copy count as
   * an attribute, so the server gives one line ONE answer: any garment of it drawing a fresh blank, or
   * reported without naming a film, forces every print for ALL of them. Computing this per garment made
   * the panel promise "back only" for a garment that would come off the sheet with both films — the
   * panel contradicting the sheet, which is the exact failure the shared function exists to prevent.
   *
   * Residual, deliberately: a garment of the same line already queued by ANOTHER station can widen the
   * real answer further. That direction is more printing, so the sentence errs toward under-promising
   * the saving rather than over-promising it.
   */
  const selectionScopeByLine = new Map<string, ReturnType<typeof reprintPlacementsFor>>();
  for (const lineKey of new Set(selectedGarments.map((g) => g.lineKey))) {
    selectionScopeByLine.set(
      lineKey,
      reprintPlacementsFor(selectedGarments.filter((g) => g.lineKey === lineKey).map((g) => g.row)),
    );
  }

  return createPortal(
    <div
      id="reprint-picker"
      role="dialog"
      aria-modal="true"
      aria-label={`Reprint a transfer — ${label}`}
      className="fixed inset-0 z-50 overflow-auto bg-background p-4 sm:p-6"
    >
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold">Reprint a transfer — {label}</h3>
            <p className="text-xs text-muted-foreground">
              Tick the film that was ruined at the bench. Nothing prints now — it joins the shared
              reprint queue.
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="size-4" />
            Close
          </Button>
        </div>

        {manifestQuery.isPending && <Skeleton className="h-40 w-full" />}
        {manifestQuery.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn't load the sheet</AlertTitle>
            <AlertDescription>
              {manifestQuery.error instanceof Error
                ? manifestQuery.error.message
                : 'Network error.'}
            </AlertDescription>
          </Alert>
        )}

        {m && blocked && (
          <Alert>
            <AlertTitle>{blocked.title}</AlertTitle>
            <AlertDescription>{blocked.detail}</AlertDescription>
          </Alert>
        )}

        {m && !blocked && (
          <>
            {m.oversizedCount > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  {m.oversizedCount} design{m.oversizedCount === 1 ? '' : 's'} won't fit the roll
                  and {m.oversizedCount === 1 ? 'is' : 'are'} missing from this list — re-check the
                  batch.
                </AlertDescription>
              </Alert>
            )}
            {m.cancelledTransferCount > 0 && (
              <Alert variant="destructive">
                <AlertTitle>
                  {m.cancelledTransferCount} transfer{m.cancelledTransferCount === 1 ? '' : 's'}{' '}
                  {m.cancelledTransferCount === 1 ? 'is' : 'are'} for a CANCELLED order — do not
                  print
                </AlertTitle>
                <AlertDescription>
                  The order was cancelled after this sheet was built. Its rows are shaded below and
                  cannot be reported.
                </AlertDescription>
              </Alert>
            )}
            {m.qtyDroppedTransferCount > 0 && (
              <Alert variant="destructive">
                <AlertTitle>
                  {m.qtyDroppedTransferCount} transfer
                  {m.qtyDroppedTransferCount === 1 ? '' : 's'}{' '}
                  {m.qtyDroppedTransferCount === 1 ? 'was' : 'were'} refunded after building — do
                  not press
                </AlertTitle>
                <AlertDescription>
                  Those copies are on the film but no longer owed. Their rows are shaded below and
                  cannot be reported.
                </AlertDescription>
              </Alert>
            )}
            {/* Per-garment outcome. A partial add is the normal failure here (one POST per
                garment, no bulk route), and a garment with no queue row has nothing blocking its
                order — so the ones that did NOT land are named, not summarised away. */}
            {addOutcomes.length > 0 && (
              <Alert variant={addOutcomes.some((o) => !o.ok) ? 'destructive' : 'default'}>
                <AlertTitle>
                  {addOutcomes.filter((o) => o.ok).length} of {addOutcomes.length} added to the
                  reprint queue
                </AlertTitle>
                <AlertDescription>
                  <ul className="space-y-0.5">
                    {addOutcomes.map((o) => (
                      <li key={o.key}>
                        {o.ok
                          ? `✓ ${o.label}${o.merged ? ' — another station had already reported it; merged' : ''}`
                          : `✗ ${o.label} — ${o.detail}`}
                        {o.ok && o.otherRuns ? (
                          <span className="block text-amber-600">
                            ⚠️ This line already has {o.otherRuns} garment
                            {o.otherRuns === 1 ? '' : 's'} waiting from another production run. If
                            that is the same shirt, it will build a second sheet — check the reprint
                            queue before printing.
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {addOutcomes.every((o) => o.ok) && (
                    <p className="mt-1">
                      Nothing printed yet. They wait in the reprint queue and merge onto one sheet
                      with every other station's reprints.
                    </p>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {selectedGarments.length > 0 && (
              <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-sm font-medium">
                    {selectedGarments.length} garment{selectedGarments.length === 1 ? '' : 's'}{' '}
                    selected to reprint
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelected(new Map());
                        setKeepBlank(new Set());
                        setAddOutcomes([]);
                      }}
                      disabled={queueAddMut.isPending}
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => queueAddMut.mutate()}
                      disabled={queueAddMut.isPending}
                    >
                      {queueAddMut.isPending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Repeat2 className="size-4" />
                      )}
                      Add {selectedGarments.length} to the reprint queue
                    </Button>
                  </div>
                </div>
                <div className="space-y-1 border-t pt-2">
                  <div className="text-xs font-medium text-muted-foreground">
                    Keep the blank? Ticked means the shirt is fine and only the films you selected
                    are reprinted — the usual case here, since cutting happens before pressing.
                    UNTICK it if the shirt itself is ruined: a fresh blank always gets every print,
                    because you cannot press a bare front.
                  </div>
                  {selectedGarments.map((g) => {
                    // The LINE's scope — every garment of a line shows the same answer, because the
                    // server gives the line one answer. See `selectionScopeByLine`.
                    const scope = selectionScopeByLine.get(g.lineKey);
                    return (
                      <label
                        key={g.key}
                        htmlFor={`keep-blank-${g.key}`}
                        className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm"
                      >
                        <Checkbox
                          id={`keep-blank-${g.key}`}
                          checked={keepBlank.has(g.key)}
                          onCheckedChange={() => toggleKeepBlank(g.key)}
                          disabled={queueAddMut.isPending}
                        />
                        <span>{g.label}</span>
                        {/* The consequence, at the control that decides it. Amber whenever a fresh
                            blank is drawn, because that is the branch that spends a shirt AND puts
                            every print back on film. */}
                        {scope && (
                          <span
                            className={
                              scope.transferOnly
                                ? 'text-xs text-muted-foreground'
                                : 'text-xs font-medium text-amber-600 dark:text-amber-500'
                            }
                          >
                            {scope.transferOnly
                              ? `reprints ${scope.placements.map((p) => p.location).join(' + ')}${
                                  scope.narrowed ? ' only' : ''
                                } · no replacement shirt`
                              : `reprints ALL ${scope.placements.length} print${
                                  scope.placements.length === 1 ? '' : 's'
                                } · draws a replacement shirt`}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">(rot)</span> marks a design the nester
              turned 90° to pack the film tighter — it doesn't change how the transfer presses onto
              the shirt. Tick the individual transfer you ruined — if a garment's back tore and its
              front is fine, tick only the back and only the back is reprinted. Nothing prints now:
              the garments join the shared{' '}
              <span className="font-medium text-foreground">reprint queue</span> and come off it on
              ONE sheet with the press station's and the office's reprints. Each keeps its shirt by
              default, since a film torn at the bench can't have damaged it — untick{' '}
              <span className="font-medium text-foreground">Keep the blank</span> for a garment
              whose shirt is ruined too, and it reprints every print onto a fresh one.
            </p>
            {sheets.map((group) => (
              <div key={group.sheetIndex} className="space-y-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Sheet {group.sheetIndex + 1} · {group.transfers.length} transfer
                  {group.transfers.length === 1 ? '' : 's'}
                </h4>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <Th className="w-8">{null}</Th>
                        <Th className="text-right">#</Th>
                        <Th>Order</Th>
                        <Th>Garment</Th>
                        <Th>Location</Th>
                        <Th>Design</Th>
                        <Th className="text-right">Size (in)</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.transfers.map((t) => {
                        // One queue row per GARMENT (line + copyIndex) — that is the queue's
                        // identity, so the cutter and the presser reporting one shirt stay one row
                        // and draw one blank. But the tick is per TRANSFER: the row records WHICH
                        // films were ruined, and a garment's other rows no longer tick with it.
                        // Built by `garmentKey` — the same helper the keep-blank state and the add
                        // bodies use, so the three cannot drift.
                        const key = garmentKey(t);
                        // A cancelled/refunded transfer isn't owed — never offer to reprint it.
                        const selectable = !(t.lineCancelled || t.lineQtyDropped);
                        return (
                          <tr
                            key={t.index}
                            className={
                              t.lineCancelled || t.lineQtyDropped
                                ? 'border-t bg-destructive/10'
                                : 'border-t [&:nth-child(even)]:bg-muted/20'
                            }
                          >
                            <td className="px-2 py-1.5 align-top">
                              {selectable && (
                                <Checkbox
                                  checked={
                                    selected.get(key)?.locations.includes(t.location) ?? false
                                  }
                                  // Locked while an add is in flight. `onSuccess` replaces the
                                  // selection wholesale with only what did NOT land, so a film
                                  // ticked mid-request would be silently discarded — the operator
                                  // would believe they had reported it.
                                  disabled={queueAddMut.isPending}
                                  onCheckedChange={() =>
                                    toggleTransfer(key, {
                                      storeId: t.storeId,
                                      orderId: t.orderId,
                                      lineId: t.lineId,
                                      copyIndex: t.copyIndex,
                                      // THE film that was ruined — exactly the row that was
                                      // clicked. `toggleTransfer` unions it into whatever this
                                      // garment already has ticked.
                                      locations: [t.location],
                                      allLocations: locationsByGarment.get(key) ?? [t.location],
                                    })
                                  }
                                  aria-label={`Report the ${t.location} transfer of garment ${t.copyIndex + 1} (transfer ${t.index}) for reprint`}
                                />
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right align-top font-medium tabular-nums">
                              {t.index}
                            </td>
                            <td className="px-3 py-1.5 align-top font-medium">
                              <Link
                                to="/orders/$storeId/$orderId"
                                params={{ storeId: t.storeId, orderId: t.orderId }}
                                search={orderCardSearch('batches')}
                                className="underline underline-offset-2 hover:no-underline"
                                title="Open this order's job card"
                              >
                                {t.orderName}
                              </Link>
                              {t.lineCancelled && (
                                <span className="mt-0.5 block text-[10px] font-bold uppercase tracking-wide text-destructive">
                                  Cancelled — do not print
                                </span>
                              )}
                              {!t.lineCancelled && t.lineQtyDropped && (
                                <span className="mt-0.5 block text-[10px] font-bold uppercase tracking-wide text-destructive">
                                  Refunded — do not press
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 align-top text-xs">
                              {t.title}
                              {t.variantTitle && (
                                <span className="text-muted-foreground"> · {t.variantTitle}</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 align-top text-xs">
                              {t.location}
                              {t.rot && <span className="ml-1 text-muted-foreground">(rot)</span>}
                            </td>
                            <td className="px-3 py-1.5 align-top text-xs">
                              {t.designName ?? (
                                <span className="font-mono text-muted-foreground">
                                  {t.designId.slice(0, 8)}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right align-top tabular-nums text-muted-foreground">
                              {formatInches(t.widthIn)} × {formatInches(t.heightIn)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
